import { useEffect, useState } from "react";
import axios from "axios";
import {
  Bot,
  History,
  Eye,
  GitCompare,
  RotateCcw,
  CheckCircle2,
  Loader2,
  X,
  AlertTriangle,
  RefreshCw,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function authHeaders() {
  const t = localStorage.getItem("token");
  return { headers: { Authorization: `Bearer ${t}` } };
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminPromptHistoryPanel() {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [viewing, setViewing] = useState(null); // full version object
  const [diffing, setDiffing] = useState(null); // { key, version_id }
  const [diffData, setDiffData] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [confirmRollback, setConfirmRollback] = useState(null); // version object
  const [rolling, setRolling] = useState(false);
  const [flashMessage, setFlashMessage] = useState(null); // { type, text }

  const load = async () => {
    setLoading(true);
    try {
      const [hist, ev] = await Promise.all([
        axios.get(`${API}/admin/prompts/history`, authHeaders()),
        axios.get(`${API}/admin/prompts/events`, authHeaders()),
      ]);
      setVersions(hist.data?.versions || []);
      setEvents(ev.data || []);
    } catch (e) {
      setFlashMessage({ type: "error", text: e.response?.data?.detail || "Failed to load." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openView = async (v) => {
    try {
      const res = await axios.get(`${API}/admin/prompts/version/${v.id}`, authHeaders());
      setViewing(res.data);
    } catch {
      setFlashMessage({ type: "error", text: "Could not load version content." });
    }
  };

  const openDiff = async (v) => {
    setDiffing({ key: v.key, version_id: v.id, version: v.version });
    setDiffLoading(true);
    setDiffData(null);
    try {
      const res = await axios.get(
        `${API}/admin/prompts/${encodeURIComponent(v.key)}/diff?version_id=${encodeURIComponent(v.id)}`,
        authHeaders()
      );
      setDiffData(res.data);
    } catch (e) {
      setFlashMessage({
        type: "error",
        text: e.response?.data?.detail || "Could not load diff.",
      });
      setDiffing(null);
    } finally {
      setDiffLoading(false);
    }
  };

  const performRollback = async () => {
    if (!confirmRollback) return;
    setRolling(true);
    try {
      await axios.post(
        `${API}/admin/prompts/${confirmRollback.id}/rollback`,
        {},
        authHeaders()
      );
      setFlashMessage({ type: "success", text: "LemonBot prompt rollback complete ✅" });
      setConfirmRollback(null);
      await load();
      setTimeout(() => setFlashMessage(null), 4000);
    } catch (e) {
      setFlashMessage({
        type: "error",
        text: e.response?.data?.detail || "Rollback failed.",
      });
    } finally {
      setRolling(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-prompt-history-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Bot className="w-5 h-5 text-amber-500" />
            LemonBot Prompt History
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Browse, compare, and roll back AI persona versions. Read-only — no editing here.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm font-medium px-3 py-1.5 rounded-lg disabled:opacity-50"
          data-testid="prompt-history-refresh-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Refresh
        </button>
      </div>

      {flashMessage && (
        <div
          className={`rounded-xl border p-3 text-sm font-medium ${
            flashMessage.type === "success"
              ? "bg-emerald-50 border-emerald-200 text-emerald-800"
              : "bg-rose-50 border-rose-200 text-rose-800"
          }`}
          data-testid="prompt-history-flash"
        >
          {flashMessage.text}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : versions.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-gray-500">
          <History className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          No prompt versions stored yet. Run the config sync to create the first record.
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-left">Key</th>
                <th className="px-4 py-2 text-left">Version</th>
                <th className="px-4 py-2 text-left">Hash</th>
                <th className="px-4 py-2 text-left">Updated</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {versions.map((v) => (
                <tr
                  key={v.id}
                  className="hover:bg-gray-50/60"
                  data-testid={`prompt-row-${v.id.slice(0, 6)}`}
                >
                  <td className="px-4 py-2.5">
                    {v.active ? (
                      <span
                        className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 text-xs font-bold rounded-full px-2 py-0.5"
                        data-testid={`prompt-active-badge-${v.id.slice(0, 6)}`}
                      >
                        <CheckCircle2 className="w-3 h-3" />
                        ACTIVE
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400 font-semibold">archived</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-gray-700">{v.key}</td>
                  <td className="px-4 py-2.5 font-bold text-gray-900">{v.version}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-gray-500">
                    {(v.content_hash || "").slice(0, 10)}…
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-500">{formatDate(v.updated_at)}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => openView(v)}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-gray-700 hover:text-gray-900 px-2 py-1 rounded-md hover:bg-gray-100"
                        data-testid={`prompt-view-${v.id.slice(0, 6)}`}
                      >
                        <Eye className="w-3.5 h-3.5" />
                        View
                      </button>
                      <button
                        onClick={() => openDiff(v)}
                        disabled={v.active}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:text-blue-900 px-2 py-1 rounded-md hover:bg-blue-50 disabled:opacity-30 disabled:cursor-not-allowed"
                        data-testid={`prompt-diff-${v.id.slice(0, 6)}`}
                        title={v.active ? "This is already the active version" : "Compare to active"}
                      >
                        <GitCompare className="w-3.5 h-3.5" />
                        Compare
                      </button>
                      {!v.active && (
                        <button
                          onClick={() => setConfirmRollback(v)}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 hover:text-amber-900 px-2 py-1 rounded-md hover:bg-amber-50"
                          data-testid={`prompt-rollback-${v.id.slice(0, 6)}`}
                        >
                          <RotateCcw className="w-3.5 h-3.5" />
                          Rollback
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Audit log */}
      {events.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <h3 className="text-sm font-bold text-gray-900 mb-2.5 flex items-center gap-1.5">
            <History className="w-4 h-4 text-gray-500" />
            Recent rollback events
          </h3>
          <ul className="text-sm divide-y divide-gray-100">
            {events.slice(0, 10).map((e) => (
              <li key={e.id} className="py-2 flex items-start gap-3">
                <RotateCcw className="w-3.5 h-3.5 text-amber-600 mt-1 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-gray-800 truncate">
                    {e.previous_active_version || "—"} → <strong>{e.new_active_version}</strong>
                  </div>
                  <div className="text-xs text-gray-500">
                    {e.key} · by {e.admin_name || e.admin_id} · {formatDate(e.timestamp)}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* View modal */}
      {viewing && (
        <Modal onClose={() => setViewing(null)} title={`${viewing.key} · ${viewing.version}`}>
          <div className="text-xs text-gray-500 mb-2 font-mono">
            hash: {viewing.content_hash} · {viewing.active ? "ACTIVE" : "archived"}
          </div>
          <pre className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs whitespace-pre-wrap font-mono max-h-[60vh] overflow-y-auto">
            {viewing.content}
          </pre>
        </Modal>
      )}

      {/* Diff modal */}
      {diffing && (
        <Modal
          onClose={() => {
            setDiffing(null);
            setDiffData(null);
          }}
          title={`Compare ${diffing.version} → ACTIVE`}
        >
          {diffLoading || !diffData ? (
            <div className="flex justify-center py-8 text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : diffData.is_same ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800">
              No differences — this version's content is identical to the active version.
            </div>
          ) : diffData.diff.length === 0 ? (
            <div className="text-sm text-gray-500 italic">No diff lines returned.</div>
          ) : (
            <pre className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs font-mono max-h-[60vh] overflow-y-auto leading-relaxed">
              {diffData.diff.map((line, i) => {
                const cls = line.startsWith("+++") || line.startsWith("---")
                  ? "text-gray-500 font-bold"
                  : line.startsWith("+")
                  ? "text-emerald-700 bg-emerald-50/60"
                  : line.startsWith("-")
                  ? "text-rose-700 bg-rose-50/60"
                  : line.startsWith("@@")
                  ? "text-blue-700 font-bold"
                  : "text-gray-700";
                return (
                  <div key={i} className={cls}>
                    {line || " "}
                  </div>
                );
              })}
            </pre>
          )}
        </Modal>
      )}

      {/* Rollback confirm modal */}
      {confirmRollback && (
        <Modal
          onClose={() => setConfirmRollback(null)}
          title="Confirm rollback"
        >
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-gray-700 leading-relaxed">
              Are you sure you want to make this LemonBot prompt version active?
              <div className="mt-2 bg-gray-50 border border-gray-200 rounded-lg p-2 font-mono text-xs">
                <div><strong>Key:</strong> {confirmRollback.key}</div>
                <div><strong>Version:</strong> {confirmRollback.version}</div>
                <div><strong>Hash:</strong> {(confirmRollback.content_hash || "").slice(0, 16)}…</div>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Student conversations will not be affected. The change takes effect on the
                next chat request.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setConfirmRollback(null)}
              disabled={rolling}
              className="px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-100 rounded-lg"
              data-testid="prompt-rollback-cancel-btn"
            >
              Cancel
            </button>
            <button
              onClick={performRollback}
              disabled={rolling}
              className="inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold px-4 py-1.5 rounded-lg disabled:opacity-60"
              data-testid="prompt-rollback-confirm-btn"
            >
              {rolling ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
              {rolling ? "Rolling back…" : "Rollback"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Modal({ children, onClose, title }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[92vh] overflow-hidden flex flex-col">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-bold text-gray-900">{title}</h3>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-gray-100"
            data-testid="prompt-modal-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}
